import type { RadioSelectorItemProps } from "../../../../../../mads/RadioSelectList/RadioSelectorItem";
import type { RootStackParamList } from "../../../../navigation/types";

import { useReportContentForm } from "./ReportHooks";
import {
  Alert,
  Button,
  TextArea,
  Box,
  Text,
  RadioSelectorList,
  ScrollView,
} from "../../../../../../mads";
import { ScreenKeyboardAvoidingView } from "../../../../../../shared/components/ScreenKeyboardAvoidingView/ScreenKeyboardAvoidingView";
import { useI18n } from "../../../../../../shared/I18n";
import { DatadogLogger } from "../../../../../../shared/logging/DatadogLogger";

import { spacing } from "@indeed/ifl-tokens/react-native";
import { type RouteProp, useNavigation } from "@react-navigation/native";
import React, { useEffect, useMemo } from "react";
import { StyleSheet } from "react-native";
import {
  initialWindowMetrics,
  SafeAreaProvider,
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

const USER_FEEDBACK_MAX_LENGTH = 300;

export interface ReportContentModalProps {
  isOpen: boolean;
  jobTitle?: string;
  companyName?: string;
  showPhotoLabel: boolean;
  withTopSafeArea: boolean;
}

type ReportContentScreenRouteProps = {
  route: RouteProp<RootStackParamList, "ReportContent">;
};

export function ReportContentModal({ route }: ReportContentScreenRouteProps) {
  const { jobTitle, companyName, showPhotoLabel } = route.params;

  const gt = useI18n();
  const navigation = useNavigation();
  const form = useReportContentForm(() => navigation.goBack(), showPhotoLabel, gt);
  const { bottom } = useSafeAreaInsets();

  const exceedsCharacterLimit = useMemo(
    () => form.moreInfo.length > USER_FEEDBACK_MAX_LENGTH,
    [form.moreInfo],
  );

  // TODO: figure out a automatic way star and stop view for react navigation views
  useEffect(() => {
    void DatadogLogger.startView("ReportContentModal");

    return () => {
      void DatadogLogger.stopView("ReportContentModal");
    };
  }, []);

  const radioSelectorProps: RadioSelectorItemProps[] = form.contentProblemOptions.map((option) => ({
    labelText: option.label,
    onSelect: () => {},
  }));

  return (
    <SafeAreaProvider initialMetrics={initialWindowMetrics}>
      <SafeAreaView style={{ flex: 1 }} edges={["left", "right", "bottom"]}>
        <ScreenKeyboardAvoidingView safeAreaBottomInset={bottom}>
          <ScrollView
            sx={styles.content}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="interactive"
            automaticallyAdjustKeyboardInsets={false}
          >
            <Box sx={{ paddingBottom: bottom }}>
              <RadioSelectorList
                labelText={jobTitle ?? ""}
                explainerText={companyName ?? undefined}
                radioSelectorProps={radioSelectorProps}
                selectedIndex={form.contentProblemOptions.findIndex(
                  (option) => option.value === form.selectedOption?.value,
                )}
                onSelect={(index) => {
                  const selectedOption = form.contentProblemOptions[index];
                  if (selectedOption) {
                    form.selectProblemOption(selectedOption);
                  }
                }}
              />
              <TextArea
                label={gt.pgettext("report job additional info box", "Additional information")}
                onInput={form.setMoreInfo}
                counterLimit={USER_FEEDBACK_MAX_LENGTH}
                value={form.moreInfo}
                textAreaHeight={80}
                invalid={exceedsCharacterLimit}
                invalidText={gt.gettext("Invalid")}
                errorText={
                  exceedsCharacterLimit
                    ? gt.gettext("Please shorten your response to {0} characters.", [String(300)])
                    : undefined
                }
              />
              <Box sx={{ marginTop: spacing.s3, marginBottom: spacing.s3 }}>
                <Alert
                  status="info"
                  title={gt.gettext("Protect your privacy")}
                  explainerText={gt.gettext(
                    "Avoid disclosing personal information like your name, phone number, or any details that may personally identify you.",
                  )}
                />
              </Box>

              <Button size="lg" onPress={form.submit} disabled={!form.canSubmit}>
                {gt.gettext("Submit")}
              </Button>
              <Text level={1} sx={{ marginTop: 4, flexGrow: 1 }}>
                {gt.pgettext(
                  "report job legal disclaimer",
                  "All Job Ads are subject to Indeed's Terms of Service. We allow users to flag postings that may be in violation of those terms. Job Ads may also be flagged by Indeed. However, no moderation system is perfect, and flagging a posting does not ensure that it will be removed.",
                )}
              </Text>
            </Box>
          </ScrollView>
        </ScreenKeyboardAvoidingView>
      </SafeAreaView>
    </SafeAreaProvider>
  );
}

const styles = StyleSheet.create({
  content: {
    paddingLeft: spacing.s4,
    paddingRight: spacing.s4,
  },
});
